/**
 * arbi find <query...> — document search without LLM
 *
 * Calls POST /v1/assistant/retrieve for search-only results.
 * Supports semantic, keyword, and hybrid search modes.
 * Output can be grouped by document (default) or flat sorted by relevance.
 */

import { Command } from 'commander'
import { assistant } from '@arbidocs/sdk'
import { runAction, resolveWorkspace } from '../helpers.js'
import { chalk } from '../output.js'

/**
 * Mirrors src/lib/retrieve-limits.ts in the webapp. Kept inline because the
 * CLI package doesn't depend on `src/`. If you change the limit here, change
 * it there too.
 */
const MAX_INDIVIDUALLY_SELECTED_DOCS = 5000

interface FindOpts {
  workspace?: string
  mode?: 'semantic' | 'keyword' | 'hybrid'
  docs?: string
  minScore?: string
  limit?: string
  flat?: boolean
  toc?: string
  fullContext?: string
  json?: boolean
}

/** Pick the best available score from a chunk. */
function chunkScore(chunk: assistant.Chunk): number {
  return chunk.metadata.rerank_score ?? chunk.metadata.score ?? 0
}

/** Truncate text to a max length, appending ellipsis if needed. */
function truncate(text: string, max: number): string {
  const oneLine = text.replace(/\n/g, ' ').trim()
  return oneLine.length > max ? oneLine.slice(0, max - 1) + '…' : oneLine
}

/** Format a score as a percentage string like "[92%]". */
function fmtScore(score: number): string {
  const pct = Math.min(Math.round(score * 100), 100)
  return `[${pct}%]`
}

export function registerFindCommand(program: Command): void {
  program
    .command('find <query...>')
    .description('Search documents without LLM generation (retrieve only)')
    .option('-w, --workspace <id>', 'Workspace ID (defaults to selected workspace)')
    .option('-m, --mode <mode>', 'Search mode: semantic, keyword, hybrid', 'semantic')
    .option(
      '-d, --docs <ids>',
      `Comma-separated document IDs to search within (omit to search the whole workspace; max ${MAX_INDIVIDUALLY_SELECTED_DOCS} when set)`
    )
    .option('--min-score <n>', 'Minimum relevance score 0-1', '0.2')
    .option('-l, --limit <n>', 'Max results to display', '20')
    .option('--flat', 'Flat list sorted by relevance (instead of grouped by document)')
    .option('--toc <ids>', 'Comma-separated doc IDs to fetch table of contents for')
    .option('--full-context <ids>', 'Comma-separated doc IDs to fetch full content for')
    .option('--json', 'Raw JSON output for scripting')
    .action((words: string[], opts: FindOpts) =>
      runAction(async () => {
        const query = words.join(' ')
        const { arbi, workspaceId } = await resolveWorkspace(opts.workspace)

        // Resolve document IDs
        //
        // No `-d` → send an empty list so the backend searches the whole
        // workspace. The previous behaviour (list every doc client-side and
        // stuff every ID into the request body) blew up on large workspaces:
        // a 35 k-doc workspace produced an ~500 KB request body and a server
        // 500 in milliseconds. The empty-list path is workspace-wide on the
        // server and stays a few hundred bytes on the wire.
        let docIds: string[]
        if (opts.docs) {
          docIds = opts.docs
            .split(',')
            .map((id) => id.trim())
            .filter((id) => id.length > 0)
          if (docIds.length > MAX_INDIVIDUALLY_SELECTED_DOCS) {
            console.error(
              chalk.red(
                `Error: ${docIds.length.toLocaleString()} document IDs supplied — maximum is ${MAX_INDIVIDUALLY_SELECTED_DOCS.toLocaleString()}.\n` +
                  'Narrow your selection, or omit -d to search the whole workspace.'
              )
            )
            process.exitCode = 1
            return
          }
        } else {
          docIds = []
        }

        const searchMode = (opts.mode ?? 'semantic') as 'semantic' | 'keyword' | 'hybrid'
        const tocDocIds = opts.toc ? opts.toc.split(',').map((id) => id.trim()) : undefined
        const fullContextDocIds = opts.fullContext
          ? opts.fullContext.split(',').map((id) => id.trim())
          : undefined

        const result = await assistant.retrieve({
          arbi,
          workspaceId,
          query,
          docIds,
          searchMode,
          tocDocIds,
          fullContextDocIds,
        })

        // Collect all chunks from retrieval_chunk responses
        const allChunks: assistant.Chunk[] = []
        if (result.retrieval_chunk?.tool_responses) {
          for (const chunks of Object.values(result.retrieval_chunk.tool_responses)) {
            allChunks.push(...chunks)
          }
        }

        // Filter by min score and sort descending
        const minScore = parseFloat(opts.minScore ?? '0.2')
        const limit = parseInt(opts.limit ?? '20', 10)

        const filtered = allChunks
          .filter((c) => chunkScore(c) >= minScore)
          .sort((a, b) => chunkScore(b) - chunkScore(a))
          .slice(0, limit)

        // Count unique documents in results
        const uniqueDocs = new Set(filtered.map((c) => c.metadata.doc_ext_id))

        // ── JSON output ──────────────────────────────────────────────────
        // Old behavior dumped the raw retrieval envelope. That's noisy and
        // inconsistent with `arbi docs --json` (which returns a clean array).
        // Emit the filtered, sorted chunks under `results` plus the
        // unfiltered raw payload under `raw` for the rare consumer that
        // wants the retrieval-tool metadata.
        if (opts.json) {
          console.log(
            JSON.stringify(
              {
                results: filtered,
                count: filtered.length,
                unique_documents: uniqueDocs.size,
                raw: result,
              },
              null,
              2
            )
          )
          return
        }

        if (filtered.length === 0) {
          process.stderr.write('No results found.\n')
          return
        }

        console.log(
          `\nFound ${chalk.bold(String(filtered.length))} result${filtered.length === 1 ? '' : 's'} across ${chalk.bold(String(uniqueDocs.size))} document${uniqueDocs.size === 1 ? '' : 's'}\n`
        )

        // ── Flat output ──────────────────────────────────────────────────
        if (opts.flat) {
          for (let i = 0; i < filtered.length; i++) {
            const chunk = filtered[i]
            const score = fmtScore(chunkScore(chunk))
            const doc = chunk.metadata.doc_title ?? chunk.metadata.doc_ext_id ?? ''
            const page = chunk.metadata.page_number ? `p.${chunk.metadata.page_number}` : ''
            const preview = truncate(chunk.content, 80)

            console.log(
              `  ${chalk.dim(String(i + 1).padStart(2, ' '))}. ${chalk.yellow(score)} ${chalk.cyan(doc)} ${chalk.dim(page)}`
            )
            console.log(`     ${chalk.dim(preview)}`)
          }
          console.log()
          return
        }

        // ── Grouped output (default) ─────────────────────────────────────
        const grouped = new Map<string, assistant.Chunk[]>()
        for (const chunk of filtered) {
          const key = chunk.metadata.doc_ext_id ?? 'unknown'
          if (!grouped.has(key)) grouped.set(key, [])
          grouped.get(key)!.push(chunk)
        }

        for (const [, chunks] of grouped) {
          const docTitle =
            chunks[0].metadata.doc_title ?? chunks[0].metadata.doc_ext_id ?? 'Unknown'
          const hitCount = chunks.length
          const header = `${docTitle} (${hitCount} hit${hitCount === 1 ? '' : 's'})`

          console.log(
            `${chalk.dim('──')} ${chalk.bold(header)} ${chalk.dim('─'.repeat(Math.max(0, 50 - header.length)))}`
          )

          for (const chunk of chunks) {
            const score = fmtScore(chunkScore(chunk))
            const page = chunk.metadata.page_number ? `p.${chunk.metadata.page_number}` : ''
            const preview = truncate(chunk.content, 72)

            console.log(
              `  ${chalk.yellow(score)} ${chalk.dim(page.padEnd(6))} ${chalk.dim(preview)}`
            )
          }
          console.log()
        }
      })()
    )
}
